/**
 * Which words each engine REFUSES to read as a bare ORDINARY IDENTIFIER — the
 * position a table or column name is parsed in. A fact table, not an algorithm.
 *
 * THE QUESTION, spelled out, because a list that answers two questions at once
 * is how this file shipped its first bug. Every entry must answer exactly this:
 *
 *     "If an emitted statement writes this word where a COLUMN or TABLE NAME
 *      goes, with no delimiters, does the server refuse it?"
 *
 * Not "is it a keyword" (SQLite's parser takes most of its keywords as
 * identifiers), not "is it reserved somewhere" (Oracle reserves COLUMN_VALUE
 * and NESTED_TABLE_ID for ATTRIBUTE names and takes both as ordinary columns),
 * and not "does the vendor list it" (Oracle's Appendix D still has no EXCEPT,
 * six years after 21c made it a set operator).
 *
 * Separated from `dialect.quoting.ts` (its only consumer, via
 * {@link isReservedWord}) for the reason `electron/schema-compare/
 * sqlReservedIdentifiers.ts` gives for the same split: getting one entry wrong
 * changes what an emitted statement MEANS, and a list is easier to check
 * against its source when nothing else shares the file.
 *
 * **PER ENGINE, deliberately not a union.** A union would be the safe direction
 * for a consumer that only ever ADDS delimiters — but ours does not. On
 * PostgreSQL and Oracle, delimiters SUPPRESS the fold, so over-delimiting is
 * not a cosmetic cost: `RENAME COLUMN comment TO x` is how an Oracle script
 * names the catalog's `COMMENT`, and rewriting it to `"comment"` asks for a
 * column the server does not have (ORA-00904). A word that is reserved on one
 * engine and an ordinary identifier on another — `alter` (unreserved on
 * PostgreSQL), `comment` (Oracle only), `position` (a PostgreSQL
 * col_name_keyword, legal as a bare column name, which is what
 * `rename-preview.realdb.spec.ts` seeds it for) — therefore has to be answered
 * for the engine in hand.
 *
 * **TWO POLICIES, and which one applies is a property of the engine:**
 *
 *   • EXACT — postgres, oracle. A delimiter suppresses the fold, so a spurious
 *     entry is a bug in its own right: it makes a bare destination land as a
 *     lower/upper-case column the user never asked for, and a bare reference
 *     ask for a column that does not exist. These two lists are the server's
 *     own answer and are never widened "just in case".
 *
 *     EXACT IS ALSO A CLAIM ABOUT A VERSION RANGE, and both lists name theirs:
 *     PostgreSQL's is exact from 16 on, Oracle's from 21c on. Each is exactly
 *     ONE word too wide before its floor — PostgreSQL reserved SYSTEM_USER in
 *     16, Oracle EXCEPT in 21c — and on this policy that word costs what a
 *     spurious entry always costs here. Both boundaries are MEASURED and
 *     pinned (`pg15-catalog`, `oracle-18c-probe`), including the two failures
 *     they produce, because the honest form of a known gap is a recorded one.
 *     Narrowing to the cross-version intersection instead was measured and
 *     rejected: it emits the bare word on every CURRENT server, where it is a
 *     hard syntax error / ORA-03050 — a worse trade in both directions.
 *     Selecting the list by server version is the real fix and is not
 *     implementable from here: {@link isReservedWord}'s only caller
 *     (`dialect.quoting.ts`'s `needsQuoting`) has no version, nor does any
 *     caller of THAT, and `ParseOptions.serverVersion` — the field shaped for
 *     it — is never populated in production. The nav rename path is already
 *     safe on both sides, because it passes the catalog's own spelling.
 *   • SUPERSET — mysql, mssql, sqlite. These store an identifier as typed, so a
 *     delimiter that was not needed costs nothing and a missing word is a
 *     rejected statement. Their lists are the union of every server-family
 *     member and every documented reservation. "When in doubt, add it" applies
 *     HERE and only here.
 *
 * **PINNED, so it cannot rot.** Each list is recorded in `reserved-words/
 * <engine>.vendor.json` together with the document edition or server version it
 * came from, the date it was read, and the command that reproduces it;
 * `dialect.reservedWords.test.ts` fails when a list and its fixture disagree.
 * Refreshing for a new server is a reviewable diff: re-run the fixture's own
 * `reproduce` command, replace that evidence block's words, and the test names
 * every word that has to move here.
 *
 * Sources, one per list (full provenance — including the probes, and the places
 * where a vendor document and its own parser disagree — in the fixtures):
 *   • PostgreSQL — `pg_get_keywords()` catcodes `R` and `T`, i.e. everything the
 *     grammar excludes from `ColId`; read from PostgreSQL 16.4 and confirmed
 *     byte-identical on 19beta1. 17 and 18 are INSIDE that window by inference,
 *     not measured — the committed fixtures hold pg15 / pg16 / pg19 and no
 *     17 / 18 block, so do not read this line as four measurements. 13 / 14 / 15 answer 100 words, the
 *     same set without SYSTEM_USER. This is the table Appendix C "SQL Key
 *     Words" is generated from. `col_name_keyword`s (BETWEEN, CHAR, POSITION,
 *     …) ARE legal column names and are deliberately absent.
 *     https://www.postgresql.org/docs/16/sql-keywords-appendix.html
 *   • Oracle — measured against Oracle AI Database 26ai Free 23.26.2.0.0: every
 *     word-shaped `V$RESERVED_WORDS` keyword the server refuses in column-name
 *     position (109 of 3047). The same sweep on 18c XE answers 108 — the same
 *     set without EXCEPT. Appendix D is the reference but NOT the list — it
 *     carries two attribute-only reservations and lacks EXCEPT.
 *     https://docs.oracle.com/en/database/oracle/oracle-database/23/sqlrf/Oracle-SQL-Reserved-Words.html
 *   • MySQL family — the union measured on MySQL 8.0.46, MySQL 9.5.0 and
 *     MariaDB 10.2.21 / 10.11.19 / 11.8.9 / 12.3.3 (a first-class variant
 *     here: `connection.variant`), widened by MariaDB's own reserved-word
 *     table. `information_schema.KEYWORDS` agrees on 8.0 and under-reports on
 *     9.x. https://dev.mysql.com/doc/refman/8.0/en/keywords.html
 *     https://mariadb.com/kb/en/reserved-words/
 *   • SQL Server — the documented "Reserved Keywords (Transact-SQL)" table
 *     (moniker sql-server-ver17), plus what SQL Server 2025 RTM-CU8 actually
 *     refuses. https://learn.microsoft.com/en-us/sql/t-sql/language-elements/reserved-keywords-transact-sql
 *   • SQLite — sqlite.org "SQL Keywords", all 147. SQLite tolerates most of
 *     them bare in one position and not the next (measured on 3.51.3: 60 are
 *     refused somewhere, all 60 on the page), so the whole page is the list —
 *     the same set `recreate/sqliteRecreate.ts` force-quotes, for that reason.
 *     https://www.sqlite.org/lang_keywords.html
 */

import type { SqlEngine } from '../../../sql-grammar/model';

/** Whitespace-delimited so each list stays diff-able against its fixture. */
const POSTGRES = `
ALL ANALYSE ANALYZE AND ANY ARRAY AS ASC ASYMMETRIC AUTHORIZATION BINARY BOTH
CASE CAST CHECK COLLATE COLLATION COLUMN CONCURRENTLY CONSTRAINT CREATE CROSS
CURRENT_CATALOG CURRENT_DATE CURRENT_ROLE CURRENT_SCHEMA CURRENT_TIME
CURRENT_TIMESTAMP CURRENT_USER DEFAULT DEFERRABLE DESC DISTINCT DO ELSE END
EXCEPT FALSE FETCH FOR FOREIGN FREEZE FROM FULL GRANT GROUP HAVING ILIKE IN
INITIALLY INNER INTERSECT INTO IS ISNULL JOIN LATERAL LEADING LEFT LIKE LIMIT
LOCALTIME LOCALTIMESTAMP NATURAL NOT NOTNULL NULL OFFSET ON ONLY OR ORDER OUTER
OVERLAPS PLACING PRIMARY REFERENCES RETURNING RIGHT SELECT SESSION_USER SIMILAR
SOME SYMMETRIC SYSTEM_USER TABLE TABLESAMPLE THEN TO TRAILING TRUE UNION UNIQUE
USER USING VARIADIC VERBOSE WHEN WHERE WINDOW WITH
`;

/** No COLUMN_VALUE / NESTED_TABLE_ID: Appendix D reserves both for ATTRIBUTE
 *  names only, and a live 23.26.2.0.0 instance takes `RENAME COLUMN c1 TO
 *  column_value` and stores COLUMN_VALUE. EXCEPT is here and not in Appendix D:
 *  the 21c set operator makes `… TO except` ORA-03050. */
const ORACLE = `
ACCESS ADD ALL ALTER AND ANY AS ASC AUDIT BETWEEN BY CHAR CHECK CLUSTER
COLUMN COMMENT COMPRESS CONNECT CREATE CURRENT DATE DECIMAL DEFAULT DELETE
DESC DISTINCT DROP ELSE EXCEPT EXCLUSIVE EXISTS FILE FLOAT FOR FROM GRANT
GROUP HAVING IDENTIFIED IMMEDIATE IN INCREMENT INDEX INITIAL INSERT INTEGER
INTERSECT INTO IS LEVEL LIKE LOCK LONG MAXEXTENTS MINUS MLSLABEL MODE MODIFY
NOAUDIT NOCOMPRESS NOT NOWAIT NULL NUMBER OF OFFLINE ON ONLINE OPTION OR
ORDER PCTFREE PRIOR PUBLIC RAW RENAME RESOURCE REVOKE ROW ROWID ROWNUM ROWS
SELECT SESSION SET SHARE SIZE SMALLINT START SUCCESSFUL SYNONYM SYSDATE TABLE
THEN TO TRIGGER UID UNION UNIQUE UPDATE USER VALIDATE VALUES VARCHAR VARCHAR2
VIEW WHENEVER WHERE WITH
`;

/** The union across the MySQL family — every member, every version, every
 *  position an emitted statement writes a column name in — because the app
 *  connects to all of it and a backtick is free here.
 *
 *  MySQL 8.0.46 (INTERSECT is 8.0.31+, the word whose absence sent
 *  `SELECT intersect FROM users` to the server), MySQL 9.5.0 (LIBRARY, plus
 *  QUALIFY / TABLESAMPLE / EXTERNAL / MANUAL / PARALLEL, which its own
 *  `information_schema.KEYWORDS` still calls unreserved while the parser
 *  answers ERROR 1064), and FOUR MariaDBs — 10.2.21, 10.11.19, 11.8.9,
 *  12.3.3 — because MariaDB keeps reserving and one sandbox server is one
 *  version's answer, not the family's: OFFSET in 10.6, ROW_NUMBER in 10.7,
 *  VECTOR in 11.7, CONVERSION and TO_DATE in 12.3. Words a newer server
 *  dropped stay (CUBE, MASTER_BIND and MASTER_SSL_VERIFY_SERVER_CERT on the
 *  MySQL side; ACCESSIBLE, DATABASE and SCHEMA, which MariaDB 12.3 releases
 *  and 11.8 still refuses) — an older server still refuses them.
 *
 *  PERIOD and SYSTEM are here for a position, not a version: MariaDB 10.11+
 *  takes both in `CREATE TABLE` and in `RENAME COLUMN … TO`, and refuses them
 *  in `ALTER TABLE … ADD`, which its own KB calls out ("the parser expects the
 *  VERSIONING keyword to follow"). The sweep therefore runs every position we
 *  emit and unions them.
 *
 *  GENERAL, IGNORE_SERVER_IDS, MASTER_HEARTBEAT_PERIOD and SLOW are on
 *  MariaDB's reserved-word table and refused by no server we probed. They stay
 *  for the reason MSSQL's DISK / DUMP / LOAD stay: on a superset engine the
 *  vendor's stated contract outranks today's lenience, and a backtick costs
 *  nothing.
 *
 *  NOT here: the ten extra words `sql_mode=ORACLE` reserves (MINUS, PACKAGE,
 *  ROWNUM, …). That mode changes far more than the keyword set and the MySQL
 *  grammar this app ships would misread such a script long before a rename was
 *  emitted; the fixture records the measured set so promoting it later is a
 *  one-line change. */
const MYSQL = `
ACCESSIBLE ADD ALL ALTER ANALYZE AND AS ASC ASENSITIVE BEFORE BETWEEN BIGINT
BINARY BLOB BOTH BY CALL CASCADE CASE CHANGE CHAR CHARACTER CHECK COLLATE
COLUMN CONDITION CONSTRAINT CONTINUE CONVERSION CONVERT CREATE CROSS CUBE
CUME_DIST CURRENT_DATE CURRENT_PATH CURRENT_ROLE CURRENT_TIME
CURRENT_TIMESTAMP CURRENT_USER CURSOR DATABASE DATABASES DAY_HOUR
DAY_MICROSECOND DAY_MINUTE DAY_SECOND DEC DECIMAL DECLARE DEFAULT DELAYED
DELETE DELETE_DOMAIN_ID DENSE_RANK DESC DESCRIBE DETERMINISTIC DISTINCT
DISTINCTROW DIV DOUBLE DO_DOMAIN_IDS DROP DUAL EACH ELSE ELSEIF EMPTY
ENCLOSED ESCAPED EXCEPT EXISTS EXIT EXPLAIN EXTERNAL FALSE FETCH FIRST_VALUE
FLOAT FLOAT4 FLOAT8 FOR FORCE FOREIGN FROM FULLTEXT FUNCTION GENERAL
GENERATED GET GRANT GROUP GROUPING GROUPS HAVING HIGH_PRIORITY
HOUR_MICROSECOND HOUR_MINUTE HOUR_SECOND IF IGNORE IGNORE_DOMAIN_IDS
IGNORE_SERVER_IDS IN INDEX INFILE INNER INOUT INSENSITIVE INSERT INT INT1
INT2 INT3 INT4 INT8 INTEGER INTERSECT INTERVAL INTO IO_AFTER_GTIDS
IO_BEFORE_GTIDS IS ITERATE JOIN JSON_TABLE KEY KEYS KILL LAG LAST_VALUE
LATERAL LEAD LEADING LEAVE LEFT LIBRARY LIKE LIMIT LINEAR LINES LOAD
LOCALTIME LOCALTIMESTAMP LOCK LONG LONGBLOB LONGTEXT LOOP LOW_PRIORITY
MANUAL MASTER_BIND MASTER_DEMOTE_TO_REPLICA MASTER_DEMOTE_TO_SLAVE
MASTER_HEARTBEAT_PERIOD MASTER_SSL_VERIFY_SERVER_CERT MATCH MAXVALUE
MEDIUMBLOB MEDIUMINT MEDIUMTEXT MIDDLEINT MINUTE_MICROSECOND MINUTE_SECOND
MOD MODIFIES NATURAL NOT NO_WRITE_TO_BINLOG NTH_VALUE NTILE NULL NUMERIC OF
OFFSET ON OPTIMIZE OPTIMIZER_COSTS OPTION OPTIONALLY OR ORDER OUT OUTER
OUTFILE OVER PAGE_CHECKSUM PARALLEL PARSE_VCOL_EXPR PARTITION PERCENT_RANK
PERIOD PORTION PRECISION PRIMARY PROCEDURE PURGE QUALIFY RANGE RANK READ
READS READ_WRITE REAL RECURSIVE REFERENCES REF_SYSTEM_ID REGEXP RELEASE
RENAME REPEAT REPLACE REQUIRE RESIGNAL RESTRICT RETURN RETURNING REVOKE
RIGHT RLIKE ROW ROWS ROW_NUMBER SCHEMA SCHEMAS SECOND_MICROSECOND SELECT
SENSITIVE SEPARATOR SET SHOW SIGNAL SLOW SMALLINT SPATIAL SPECIFIC SQL
SQLEXCEPTION SQLSTATE SQLWARNING SQL_AFTER_GTIDS SQL_BEFORE_GTIDS
SQL_BIG_RESULT SQL_CALC_FOUND_ROWS SQL_SMALL_RESULT SSL STARTING
STATS_AUTO_RECALC STATS_PERSISTENT STATS_SAMPLE_PAGES STORED STRAIGHT_JOIN
SYSTEM TABLE TABLESAMPLE TERMINATED THEN TINYBLOB TINYINT TINYTEXT TO
TO_DATE TRAILING TRIGGER TRUE UNDO UNION UNIQUE UNLOCK UNSIGNED UPDATE USAGE
USE USING UTC_DATE UTC_TIME UTC_TIMESTAMP VALUES VARBINARY VARCHAR
VARCHARACTER VARYING VECTOR VIRTUAL WHEN WHERE WHILE WINDOW WITH WRITE XOR
YEAR_MONTH ZEROFILL
`;

/** Microsoft's reserved-keyword table plus REGEXP_LIKE, which SQL Server 2025
 *  refuses ("Incorrect syntax near the keyword 'REGEXP_LIKE'") and the table
 *  does not yet carry. The table's only multi-word entry is `WITHIN GROUP`,
 *  kept here as WITHIN. DISK / DUMP / LOAD / PRECISION / SECURITYAUDIT /
 *  WITHIN are documented reserved but accepted bare by today's parser — they
 *  stay, because brackets cost nothing here and Microsoft's "only by using
 *  delimited identifiers" is the contract. */
const MSSQL = `
ADD ALL ALTER AND ANY AS ASC AUTHORIZATION BACKUP BEGIN BETWEEN BREAK BROWSE
BULK BY CASCADE CASE CHECK CHECKPOINT CLOSE CLUSTERED COALESCE COLLATE COLUMN
COMMIT COMPUTE CONSTRAINT CONTAINS CONTAINSTABLE CONTINUE CONVERT CREATE
CROSS CURRENT CURRENT_DATE CURRENT_TIME CURRENT_TIMESTAMP CURRENT_USER CURSOR
DATABASE DBCC DEALLOCATE DECLARE DEFAULT DELETE DENY DESC DISK DISTINCT
DISTRIBUTED DOUBLE DROP DUMP ELSE END ERRLVL ESCAPE EXCEPT EXEC EXECUTE
EXISTS EXIT EXTERNAL FETCH FILE FILLFACTOR FOR FOREIGN FREETEXT FREETEXTTABLE
FROM FULL FUNCTION GOTO GRANT GROUP HAVING HOLDLOCK IDENTITY IDENTITYCOL
IDENTITY_INSERT IF IN INDEX INNER INSERT INTERSECT INTO IS JOIN KEY KILL LEFT
LIKE LINENO LOAD MERGE NATIONAL NOCHECK NONCLUSTERED NOT NULL NULLIF OF OFF
OFFSETS ON OPEN OPENDATASOURCE OPENQUERY OPENROWSET OPENXML OPTION OR ORDER
OUTER OVER PERCENT PIVOT PLAN PRECISION PRIMARY PRINT PROC PROCEDURE PUBLIC
RAISERROR READ READTEXT RECONFIGURE REFERENCES REGEXP_LIKE REPLICATION
RESTORE RESTRICT RETURN REVERT REVOKE RIGHT ROLLBACK ROWCOUNT ROWGUIDCOL RULE
SAVE SCHEMA SECURITYAUDIT SELECT SEMANTICKEYPHRASETABLE
SEMANTICSIMILARITYDETAILSTABLE SEMANTICSIMILARITYTABLE SESSION_USER SET
SETUSER SHUTDOWN SOME STATISTICS SYSTEM_USER TABLE TABLESAMPLE TEXTSIZE THEN
TO TOP TRAN TRANSACTION TRIGGER TRUNCATE TRY_CONVERT TSEQUAL UNION UNIQUE
UNPIVOT UPDATE UPDATETEXT USE USER VALUES VARYING VIEW WAITFOR WHEN WHERE
WHILE WITH WITHIN WRITETEXT
`;

const SQLITE = `
ABORT ACTION ADD AFTER ALL ALTER ALWAYS ANALYZE AND AS ASC ATTACH AUTOINCREMENT
BEFORE BEGIN BETWEEN BY CASCADE CASE CAST CHECK COLLATE COLUMN COMMIT CONFLICT
CONSTRAINT CREATE CROSS CURRENT CURRENT_DATE CURRENT_TIME CURRENT_TIMESTAMP
DATABASE DEFAULT DEFERRABLE DEFERRED DELETE DESC DETACH DISTINCT DO DROP EACH
ELSE END ESCAPE EXCEPT EXCLUDE EXCLUSIVE EXISTS EXPLAIN FAIL FILTER FIRST
FOLLOWING FOR FOREIGN FROM FULL GENERATED GLOB GROUP GROUPS HAVING IF IGNORE
IMMEDIATE IN INDEX INDEXED INITIALLY INNER INSERT INSTEAD INTERSECT INTO IS
ISNULL JOIN KEY LAST LEFT LIKE LIMIT MATCH MATERIALIZED NATURAL NO NOT NOTHING
NOTNULL NULL NULLS OF OFFSET ON OR ORDER OTHERS OUTER OVER PARTITION PLAN PRAGMA
PRECEDING PRIMARY QUERY RAISE RANGE RECURSIVE REFERENCES REGEXP REINDEX RELEASE
RENAME REPLACE RESTRICT RETURNING RIGHT ROLLBACK ROW ROWS SAVEPOINT SELECT SET
TABLE TEMP TEMPORARY THEN TIES TO TRANSACTION TRIGGER UNBOUNDED UNION UNIQUE
UPDATE USING VACUUM VALUES VIEW VIRTUAL WHEN WHERE WINDOW WITH WITHOUT
`;

function toSet(words: string): ReadonlySet<string> {
  return new Set(words.split(/\s+/).filter((w) => w.length > 0));
}

/** Upper-cased for lookup; {@link isReservedWord} upper-cases the candidate.
 *
 *  Exported for ONE caller — `dialect.reservedWords.test.ts`, which diffs the
 *  whole set against the vendor fixture beside it. A predicate cannot be
 *  enumerated, and a list that cannot be enumerated cannot be pinned. */
export const RESERVED_WORDS: Record<SqlEngine, ReadonlySet<string>> = {
  postgres: toSet(POSTGRES),
  oracle: toSet(ORACLE),
  mysql: toSet(MYSQL),
  mssql: toSet(MSSQL),
  sqlite: toSet(SQLITE),
};

/** Whether `engine` refuses to read this spelling as a bare ORDINARY identifier
 *  — the position a table or column name is parsed in — because the word is
 *  reserved there. Case-insensitive: a catalog may hold either spelling, and
 *  the parser rejects both. */
export function isReservedWord(name: string, engine: SqlEngine): boolean {
  return RESERVED_WORDS[engine].has(name.toUpperCase());
}
